import 'CssFolder/site-management/site-management.scss';
import { Icon, Icons, Intent } from "construct-ui";
import moment from "moment";
import DatePickerComponent from "../../components/date-picker/date-picker.component";
import LocationTreeCheckboxComponent from "../../components/location/location-tree-checkbox.component";
import MultiSelectComponent from "../../components/multi-select/multi-select.component";
import paginationComponent from "../../components/pagination/pagination.component";
import SingleSelectComponent from "../../components/single-select/single-select.component";
import SelectSiteGroupComponent from "../../components/site/select-site-group.component";
import SelectSiteSearchInputComponent from "../../components/site/select-site-search-input.component";
import SelectUserComponent from "../../components/user/select-user.component";
import { SITE_STATUS, codeMasterType, formats } from "../../config/constants";
import { apiStatusCodes } from '../../config/global-variables';
import codeMasterModel from "../../models/common/code-master.model";
import DivisionModel from "../../models/division/division.model";
import SiteManagementModel from "../../models/site-management/site-management.model";
import ViewBase from "../view-base.view";
import SiteManagementInformationDialogView from "./site-management-information-dialog.view";
import {downloadUserCommonExcel} from '../../models/file.model'
/**
 * Site Management View
 * 
 * A page that displays a list of sites for users to view general information, sold to mapping, site product UDF, 
 * and to manage contacts and attachments
 * 
 * @author CV Leong
 */
export default function SiteManagementView() {
  ViewBase.call(this);

  let divisionOptions = [];
  let isShowingInfoDialog = false;
  let previousRequestParams;
  let selectedSiteId;
  let siteTypeOptions = [];
  let siteStatusOptions = [];
  let tableData = [];

  const exportingFiles = new Map();

  const DEFAULT_SITE_STATUS = SITE_STATUS.filter(i => ['Active', 'Low Active', 'Inactive'].includes(i.name));

  const DEFAULT_FILTERS = {
    division: '',
    locations: [],
    promoters: [],
    siteGroups: [],
    sites: [],
    siteStatuses: DEFAULT_SITE_STATUS,
    siteType: '',
    updateDateFrom: '',
    updateDateTo: '',
  };

  const DEFAULT_PAGINATION = {
    pageIndex: 1,
    pageSize: 10,
    totalItems: 0
  };

  const EXPORT_TYPE = {
    Excel: 'NewExcel',
    MasterRelation: 'MR',
    SalesForce: 'NewExcelProMT',
  }

  let filters = structuredClone(DEFAULT_FILTERS);
  let pagination = structuredClone(DEFAULT_PAGINATION);

  const requestParams = {
    get pageIndex() {
      return pagination.pageIndex;
    },

    get pageSize() {
      return pagination.pageSize;
    },

    get division() {
      return filters.division.code ?? '';
    },

    get locations() {
      return [...filters.locations];
    },

    get promoters() {
      return filters.promoters.map(i => i.userID);
    },

    get siteGroups() {
      return filters.siteGroups.map(i => i.code);
    },

    get siteIds() {
      return filters.sites.map(i => i.siteId);
    },

    get siteStatuses() {
      return filters.siteStatuses.map(i => i.code);
    },

    get siteType() {
      return filters.siteType.code ?? '';
    },

    get updateDateFrom() {
      return filters.updateDateFrom
        ? moment(filters.updateDateFrom, formats.dateFormat).format(formats.apiDateFormat)
        : '';
    },

    get updateDateTo() {
      return filters.updateDateTo
        ? moment(filters.updateDateTo, formats.dateFormat).format(formats.apiDateFormat)
        : '';
    },
  }

  function updateFilterValue(attr, value) {
    if (Array.isArray(filters[attr])) {
      filters[attr].splice(0, Infinity, ...value);
    } else {
      filters[attr] = value;
    }
  }

  function resetFilters() {
    filters = structuredClone(DEFAULT_FILTERS);
  }

  const clearFilter = (e) => {
    e.preventDefault();
    resetFilters();
  };

  const rowNo = (index) => {
    return pagination.pageSize * (pagination.pageIndex - 1) + (index + 1);
  }

  const search = (e) => {
    e?.preventDefault();
    this.showLoading();

    SiteManagementModel.searchSiteManagement(requestParams)
      .then((result) => {
        tableData.splice(0);
        result?.data.map((col, index) => {
          tableData.push({ rowNo: rowNo(index), ...col })
        })
        pagination.totalItems = result?.total ?? 0;

        // Save search filters of current result
        previousRequestParams = structuredClone(requestParams);
      })
      .catch((error) => {
        tableData = [];
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      })

    return false;
  }

  const getDivisionOptions = () => {
    divisionOptions.splice(0);

    DivisionModel.getDivisionList()
      .then((result) => {
        result?.map(i => {
          divisionOptions.push({ ...i })
        })
      })
      .catch((error) => {
        divisionOptions.splice(0);
        this.handleError(error);
      })
  }

  const getSiteTypeOptions = () => {
    siteTypeOptions.splice(0);

    codeMasterModel.getByCategoryV2({ category: codeMasterType.siteType })
      .then((result) => {
        result?.map(i => {
          siteTypeOptions.push({ code: i.code, name: i.name })
        })
      })
      .catch((error) => {
        siteTypeOptions.splice(0);
        this.handleError(error);
      })
  }

  const getSiteStatusOptions = () => {
    siteStatusOptions.splice(0);

    codeMasterModel.getByCategoryV2({ category: codeMasterType.siteStatus })
      .then((result) => {
        result?.map(i => {
          siteStatusOptions.push({ code: i.code, name: i.name })
        })
      })
      .catch((error) => {
        siteStatusOptions.splice(0);
        this.handleError(error);
      })
  }

  const exportResult = (exportType) => {
    if (exportingFiles.has(exportType)) {
      return false;
    }

    exportingFiles.set(exportType);

    const fileName = `Site Management - ${exportType}.xlsx`

    switch (exportType) {
      // only for Master Relation
      case EXPORT_TYPE.MasterRelation:
        const payload = {
          storedProcedureCode: "66",
          params: [
            { code: "31", value: previousRequestParams.siteIds.join("^") }, //SiteIds
            { code: "129", value: previousRequestParams.siteGroups.join("^") }, //SiteGroup
            { code: "130", value: previousRequestParams.siteStatuses.join("^") }, //SiteStatuses
            { code: "131", value: previousRequestParams.locations.join("^") }, //Locations
            { code: "132", value: previousRequestParams.siteType }, //SiteType
            { code: "100", value: previousRequestParams.promoters.join("^") }, //Promoters
            { code: "97", value: previousRequestParams.division }, //Division
            { code: "8", value: previousRequestParams.updateDateFrom }, //FromDate
            { code: "9", value: previousRequestParams.updateDateTo }, //ToDate
          ],
          excelSheetNames: ["Report"]
        }

        downloadUserCommonExcel(payload, fileName)
          .catch((error) => {
            if (error.code === apiStatusCodes.noContent) {
              this.showErrorMessage(this.getText("LT00000317", "No results found"))
            } else {
              this.handleError(error);
            }
          })
          .finally(() => {
            exportingFiles.delete(exportType);
          });

        break;

      default:
        SiteManagementModel.exportSiteManagement(exportType, previousRequestParams, fileName)
          .catch((error) => {
            if (error.code === apiStatusCodes.noContent) {
              this.showErrorMessage(this.getText("LT00000317", "No results found"))
            } else {
              this.handleError(error);
            }
          })
          .finally(() => {
            exportingFiles.delete(exportType);
          });

        break;
    }
    return false;
  }

  const getStatusClass = (statusCode) => {
    return SITE_STATUS.find((status) => status.code === statusCode)?.class
  }

  return {
    oninit: () => {
      getDivisionOptions();
      getSiteTypeOptions();
      getSiteStatusOptions();
    },
    oncreate: () => {
    },
    onremove: () => {
    },
    view: () => {
      return [
        /* SEARCH */
        m('.card.mb-3',
          m('.card-body',
            m('form.row.g-2', {
              onsubmit: search
            },
              m('.col-xl-3.col-lg-4.col-sm-6',
                m('label.form-label[for=sites]', this.getText('LT00000516', 'Site(s)')),
                m(SelectSiteSearchInputComponent, {
                  multiple: true,
                  selectedItems: filters.sites,
                  onSelect: (value) => {
                    updateFilterValue('sites', value);
                  }
                })
              ),
              m('.col-xl-3.col-lg-4.col-sm-6',
                m('label.form-label[for=siteGroups]', this.getText('LT00000517', 'Site Group(s)')),
                m(SelectSiteGroupComponent, {
                  id: 'siteGroups',
                  selectedItems: filters.siteGroups,
                  onChange: (value) => {
                    updateFilterValue('siteGroups', value);
                  }
                }),
              ),
              m('.col-xl-3.col-lg-4.col-sm-6',
                m('label.form-label[for=siteType]', this.getText('LT00000808', 'Site Type')),
                m(SingleSelectComponent, {
                  id: 'siteType',
                  items: siteTypeOptions,
                  selectedItem: filters.siteType,
                  allowEmpty: true,
                  onSelect: (value) => {
                    updateFilterValue('siteType', value);
                  }
                }),
              ),
              m('.col-xl-3.col-lg-4.col-sm-6',
                m('label.form-label[for=siteStatuses]', this.getText('LT00000807', 'Site Status')),
                m(MultiSelectComponent, {
                  id: 'siteStatuses',
                  items: siteStatusOptions,
                  selectedItems: filters.siteStatuses,
                  onSelect: (value) => {
                    updateFilterValue('siteStatuses', value);
                  }
                }),
              ),
              m('.col-xl-3.col-lg-4.col-sm-6',
                m('label.form-label[for=locations]', this.getText('LT00000557', 'Location')),
                m(LocationTreeCheckboxComponent, {
                  id: 'locations',
                  selectedCodes: filters.locations,
                  isLevel1Expanded: true,
                  onChange: (value) => {
                    updateFilterValue('locations', value);
                  }
                })
              ),
              m('.col-xl-3.col-lg-4.col-sm-6',
                m('label.form-label[for=promoters]', this.getText('LT00000561', 'Promoter')),
                m(SelectUserComponent, {
                  id: 'promoters',
                  hideUploadUsers: true,
                  selectedUserList: filters.promoters,
                  onSelect: (value) => {
                    updateFilterValue('promoters', value);
                  }
                })
              ),
              m('.col-xl-3.col-lg-4.col-sm-6',
                m('label.form-label[for=division]', this.getText('LT00000233', 'Division')),
                m(SingleSelectComponent, {
                  id: 'division',
                  items: divisionOptions,
                  selectedItem: filters.division,
                  allowEmpty: true,
                  onSelect: (value) => {
                    updateFilterValue('division', value);
                  }
                }),
              ),
              m('.col-xl-3.col-lg-4.col-sm-6',
                m('label.form-label[for=updateDate]', this.getText('LT00000306', 'Update Date')),
                m(DatePickerComponent, {
                  id: 'updateDate',
                  class: 'form-control',
                  readonly: true,
                  isRange: true,
                  dateStart: filters.updateDateFrom,
                  dateEnd: filters.updateDateTo,
                  onselect: (e) => {
                    updateFilterValue('updateDateFrom', moment(e.detail.start).format(formats.dateFormat));
                    updateFilterValue('updateDateTo', moment(e.detail.end).format(formats.dateFormat));
                  }
                }),
              ),

              // Search & clear buttons
              m('.col-12.d-flex.justify-content-end.gap-2',
                m('button.btn.btn-primary[type="submit"]', {
                }, this.getText("LT00000001", "Search")),
                m('button.btn.btn-white', {
                  onclick: clearFilter
                }, this.getText("LT00000031", "Clear")),
              ),
            )
          )
        ),


        /* TABLE */
        m('.card',
          m('.card-body',
            m('.d-flex.justify-content-end.mb-2',
              m('.input-group.w-auto',
                m('button.btn.btn-primary.dropdown-toggle.dropdown-toggle-split[type=button][data-bs-toggle=dropdown][arie-expanded=false]',
                  {
                    disabled: !previousRequestParams,
                  },
                  exportingFiles.size > 0
                    ? m('span.spinner-border.spinner-border-sm.me-2[role=status][aria-hidden=true]')
                    : m('i.bi.bi-file-earmark-arrow-down'),
                  this.getText('LT00000492', 'Download')
                ),
                m('ul.dropdown-menu',
                  m('li',
                    m('a.dropdown-item[href="javascript:void(0);"]', {
                      class: exportingFiles.has(EXPORT_TYPE.Excel) ? 'disabled' : '',
                      onclick: (e) => {
                        e.preventDefault();
                        exportResult(EXPORT_TYPE.Excel);
                      }
                    },
                      exportingFiles.has(EXPORT_TYPE.Excel) &&
                      m('span.spinner-border.spinner-border-sm.text-secondary.me-2[role=status]'),
                      this.getText('LT00000493', 'Excel')
                    )
                  ),
                  m('li',
                    m('a.dropdown-item[href="javascript:void(0);"]', {
                      class: exportingFiles.has(EXPORT_TYPE.SalesForce) ? 'disabled' : '',
                      onclick: (e) => {
                        e.preventDefault();
                        exportResult(EXPORT_TYPE.SalesForce);
                      }
                    },
                      exportingFiles.has(EXPORT_TYPE.SalesForce) &&
                      m('span.spinner-border.spinner-border-sm.text-secondary.me-2[role=status]'),
                      this.getText('LT00000832', 'SalesForce')
                    )
                  ),
                  m('li',
                    m('a.dropdown-item[href="javascript:void(0);"]', {
                      class: exportingFiles.has(EXPORT_TYPE.MasterRelation) ? 'disabled' : '',
                      onclick: (e) => {
                        e.preventDefault();
                        exportResult(EXPORT_TYPE.MasterRelation);
                      }
                    },
                      exportingFiles.has(EXPORT_TYPE.MasterRelation) &&
                      m('span.spinner-border.spinner-border-sm.text-secondary.me-2[role=status]'),
                      this.getText('LT00000833', 'Master Relation')
                    )
                  ),
                ),
              ),
            ),
            m('.row.g-2',
              m('.col-12',
                m('.table-responsive',
                  m('table.table.table-hover.sticky-column-start',
                    m('thead',
                      m('tr.align-middle',
                        m('th', this.getText('LT00000721', 'No.')),
                        m('th', this.getText('LT00000809', 'Site ID')),
                        m('th', this.getText('LT00000144', 'Site Name')),
                        m('th', this.getText('LT00000808', 'Site Type')),
                        m('th', this.getText('LT00000072', 'Site Group')),
                        m('th', this.getText('LT00000557', 'Location')),
                        m('th', this.getText('LT00000233', 'Division')),
                        m('th.text-center', this.getText('LT00000015', 'Status')),
                        m('th.text-center', this.getText('LT00000092', 'Action')),
                      )
                    ),
                    m('tbody',
                      (tableData.length === 0) &&
                      m('tr',
                        m('td[colspan=9]', this.getText("EM00000141", "No records found."))
                      ),
                      tableData.length > 0 &&
                      tableData.map((col) => [
                        m('tr.align-middle', { key: col.siteId },
                          m('td', col.rowNo),
                          m('td', col.siteId),
                          m('td', col.siteName),
                          m('td', col.siteTypeName),
                          m('td', col.siteGroup),
                          m('td', col.location),
                          m('td', col.division),
                          m('td.text-center',
                            m('.badge.rounded-pill.site-status', { class: getStatusClass(col.statusCode) }, col.status)
                          ),
                          m('td.text-center',
                            m(Icon, {
                              intent: Intent.PRIMARY,
                              name: Icons.EDIT,
                              onclick: () => {
                                selectedSiteId = col.siteId;
                                isShowingInfoDialog = true;
                              },
                              title: this.getText('LT00000128', 'Edit'),
                            }),
                          ),
                        )
                      ]),
                    )
                  )
                )
              ),
              tableData.length > 0 &&
              m('.col-12', m(paginationComponent,
                {
                  currentPage: pagination.pageIndex,
                  pageSize: pagination.pageSize,
                  totalItems: pagination.totalItems,
                  onPageChanged: (pageIndex) => {
                    pagination.pageIndex = pageIndex;
                    search();
                  },
                  onPageSizeChanged: (pageIndex, pageSize) => {
                    pagination.pageIndex = pageIndex;
                    pagination.pageSize = pageSize;
                    search();
                  }
                }
              )),
            )
          )
        ),
        m(SiteManagementInformationDialogView, {
          isOpen: isShowingInfoDialog,
          siteId: selectedSiteId,
          onClose: (o) => {
            isShowingInfoDialog = false;
          },
        }),
        this.getBaseViews()
      ];
    }
  };
}
